import type { ApiResponse as _ApiResponse } from '../types';
import { RETRY_CONFIGS } from '../constants/timings';

import { handleError } from '../utils/errorHandler';
import { getAuthHeadersFromStorage } from '../utils/httpUtils';
import { firebaseClient } from './firebase/firebase-client';
import { isE2ETestMode } from '../utils/apiMode';

/**
 * Transcription segment from speech-to-text
 */
export interface TranscriptionSegment {
  text: string;
  start: number;
  end: number;
  language: string;
}

/**
 * Options for AI-enhanced transcription
 */
export interface AIEnhancementOptions {
  enabled: boolean;
  llmProvider?: string;
  llmModel?: string;
}

/**
 * Response from transcription API
 */
export interface TranscriptionResponse {
  success: boolean;
  task_id?: string;
  job_id?: string;
  segments?: TranscriptionSegment[];
  full_text?: string;
  original_text?: string;
  detected_language?: string;
  processing_time_seconds?: number;
  segment_count?: number;
  ai_enhanced?: boolean;
  refinement_applied?: boolean;
  model_used?: string;
  error?: string;
}

/**
 * Response from refinement API
 */
export interface RefinementResponse {
  success: boolean;
  task_id?: string;
  refined_text?: string;
  original_text?: string;
  detected_language?: string;
  processing_time_seconds?: number;
  model_used?: string;
  error?: string;
}

/**
 * Supported languages response
 */
export interface SupportedLanguagesResponse {
  success: boolean;
  data?: {
    languages: Record<string, string>;
    count: number;
    auto_detect_supported: boolean;
  };
  error?: string;
}

/**
 * Job status response from polling endpoint
 */
interface JobStatusResponse {
  success: boolean;
  data?: {
    job_id: string;
    status: 'pending' | 'processing' | 'completed' | 'failed';
    progress?: number;
    message?: string;
    result_url?: string;
    error?: string;
    // The API returns transcription data in extra_metadata
    extra_metadata?: {
      segments?: TranscriptionSegment[];
      full_text?: string;
      original_text?: string;
      detected_language?: string;
      segment_count?: number;
      ai_enhanced?: boolean;
      refinement_applied?: boolean;
      model_used?: string;
      processing_time_seconds?: number;
    };
  };
  error?: string;
}

// Polling configuration
const { maxAttempts: MAX_POLLING_ATTEMPTS, intervalMs: POLLING_INTERVAL_MS } =
  RETRY_CONFIGS.TRANSCRIPTION;

/**
 * Transcription API Service
 * Handles speech-to-text operations using WhisperX via RunPod
 */
export class TranscriptionAPI {
  /**
   * Get the base URL for the API
   */
  private static async getBaseUrl(): Promise<string> {
    const { VideoGenerationAPI } = await import('./api');
    return VideoGenerationAPI.getBaseUrl();
  }

  /**
   * Poll job status until completion
   * @param jobId - Job ID to poll
   * @param onProgress - Optional progress callback
   * @returns Final job result
   */
  private static async pollJobStatus(
    jobId: string,
    onProgress?: (progress: number) => void
  ): Promise<JobStatusResponse> {
    const baseUrl = await this.getBaseUrl();
    let attempts = 0;

    while (attempts < MAX_POLLING_ATTEMPTS) {
      try {
        const response = await fetch(`${baseUrl}/api/v1/jobs/${jobId}`, {
          headers: await getAuthHeadersFromStorage(),
        });
        const data: JobStatusResponse = await response.json();

        if (!response.ok || !data.success) {
          return {
            success: false,
            error: data.error || 'Failed to get job status',
          };
        }

        const jobData = data.data;
        if (!jobData) {
          return {
            success: false,
            error: 'Invalid job status response',
          };
        }

        // Report progress
        if (onProgress && jobData.progress !== undefined) {
          onProgress(jobData.progress);
        }

        // Check if job is complete
        if (jobData.status === 'completed') {
          return data;
        }

        if (jobData.status === 'failed') {
          return {
            success: false,
            error: jobData.error || 'Transcription job failed',
          };
        }

        // Wait before next poll
        await new Promise((resolve) => setTimeout(resolve, POLLING_INTERVAL_MS));
        attempts++;
      } catch (error) {
        console.error('Polling error:', error);
        // Continue polling on transient errors
        await new Promise((resolve) => setTimeout(resolve, POLLING_INTERVAL_MS));
        attempts++;
      }
    }

    return {
      success: false,
      error: 'Transcription timed out',
    };
  }

  /**
   * Upload and transcribe a media file with optional AI enhancement
   * @param file - Video or audio file to transcribe
   * @param sourceLanguage - Optional source language code (auto-detect if not provided)
   * @param onProgress - Optional progress callback
   * @param aiEnhancement - Optional AI enhancement options
   * @returns Transcription result
   */
  static async transcribeFile(
    file: File,
    sourceLanguage?: string,
    onProgress?: (progress: number) => void,
    aiEnhancement?: AIEnhancementOptions
  ): Promise<TranscriptionResponse> {
    try {
      const baseUrl = await this.getBaseUrl();
      const formData = new FormData();
      formData.append('file', file);

      if (sourceLanguage && sourceLanguage !== 'auto') {
        formData.append('source_language', sourceLanguage);
      }

      // Add AI enhancement options if enabled
      if (aiEnhancement?.enabled) {
        formData.append('ai_enhanced', 'true');
        if (aiEnhancement.llmProvider) {
          formData.append('llm_provider', aiEnhancement.llmProvider);
        }
        if (aiEnhancement.llmModel) {
          formData.append('llm_model', aiEnhancement.llmModel);
        }
      }

      // Determine which endpoint to use
      const endpoint = aiEnhancement?.enabled
        ? `${baseUrl}/api/v1/transcription/upload-and-refine`
        : `${baseUrl}/api/v1/transcription/upload`;

      // Get auth token before XHR promise
      const authToken = await firebaseClient.getIdToken();

      // Upload file and get job ID
      const uploadResponse = await new Promise<{
        success: boolean;
        job_id?: string;
        error?: string;
      }>((resolve, reject) => {
        const xhr = new XMLHttpRequest();

        xhr.upload.addEventListener('progress', (event) => {
          if (event.lengthComputable && onProgress) {
            // Upload progress is 0-50%
            const progress = Math.round((event.loaded / event.total) * 50);
            onProgress(progress);
          }
        });

        xhr.addEventListener('load', () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            try {
              const response = JSON.parse(xhr.responseText);
              if (response.success && response.data) {
                resolve({
                  success: true,
                  job_id: response.data.job_id,
                });
              } else {
                resolve({
                  success: false,
                  error: response.error || 'Upload failed',
                });
              }
            } catch {
              reject(new Error('Failed to parse response'));
            }
          } else {
            try {
              const errorResponse = JSON.parse(xhr.responseText);
              resolve({
                success: false,
                error: errorResponse.error || `Request failed with status ${xhr.status}`,
              });
            } catch {
              resolve({
                success: false,
                error: `Request failed with status ${xhr.status}`,
              });
            }
          }
        });

        xhr.addEventListener('error', () => {
          reject(new Error('Network error occurred'));
        });

        xhr.addEventListener('abort', () => {
          reject(new Error('Upload cancelled'));
        });

        xhr.open('POST', endpoint);
        xhr.setRequestHeader('X-Requested-With', 'XMLHttpRequest');
        if (authToken) {
          xhr.setRequestHeader('Authorization', `Bearer ${authToken}`);
        }
        // E2E bypass header — independent of authToken (Playwright run may
        // or may not have one). Conditional so real users never leak it.
        if (isE2ETestMode()) {
          xhr.setRequestHeader('X-E2E-Test', 'true');
        }
        xhr.send(formData);
      });

      if (!uploadResponse.success || !uploadResponse.job_id) {
        return {
          success: false,
          error: uploadResponse.error || 'Failed to submit transcription job',
        };
      }

      // Report upload complete
      if (onProgress) {
        onProgress(50);
      }

      // Poll for job completion (progress from 50-100%)
      const jobResult = await this.pollJobStatus(uploadResponse.job_id, (progress) => {
        if (onProgress) {
          // Processing progress is 50-100%
          onProgress(50 + Math.round(progress / 2));
        }
      });

      if (!jobResult.success || !jobResult.data) {
        return {
          success: false,
          error: jobResult.error || 'Transcription failed',
        };
      }

      // Extract result from job extra_metadata (API returns data in extra_metadata)
      const metadata = jobResult.data.extra_metadata || {};

      return {
        success: true,
        task_id: uploadResponse.job_id,
        job_id: uploadResponse.job_id,
        segments: metadata.segments,
        full_text: metadata.full_text,
        original_text: metadata.original_text,
        detected_language: metadata.detected_language,
        processing_time_seconds: metadata.processing_time_seconds,
        segment_count: metadata.segment_count,
        ai_enhanced: metadata.ai_enhanced,
        refinement_applied: metadata.refinement_applied,
        model_used: metadata.model_used,
      };
    } catch (error: unknown) {
      const errorMessage = handleError(error, 'Transcription', 'Failed to transcribe file');
      return {
        success: false,
        error: errorMessage,
      };
    }
  }

  /**
   * Refine an existing transcription using LLM
   * @param text - Transcription text to refine
   * @param detectedLanguage - Detected language code
   * @param options - LLM options
   * @returns Refined transcription
   */
  static async refineTranscription(
    text: string,
    detectedLanguage: string,
    options?: {
      llmProvider?: string;
      llmModel?: string;
      segments?: TranscriptionSegment[];
    }
  ): Promise<RefinementResponse> {
    try {
      const baseUrl = await this.getBaseUrl();
      const response = await fetch(`${baseUrl}/api/v1/transcription/refine`, {
        method: 'POST',
        headers: await getAuthHeadersFromStorage(),
        body: JSON.stringify({
          text,
          detected_language: detectedLanguage,
          llm_provider: options?.llmProvider || 'openrouter',
          llm_model: options?.llmModel || null,
          segments: options?.segments || null,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          error: data.error || 'Refinement failed',
        };
      }

      if (data.success && data.data) {
        return {
          success: true,
          task_id: data.data.task_id,
          refined_text: data.data.refined_text,
          original_text: data.data.original_text,
          detected_language: data.data.detected_language,
          processing_time_seconds: data.data.processing_time_seconds,
          model_used: data.data.model_used,
        };
      }

      return {
        success: false,
        error: data.error || 'Refinement failed',
      };
    } catch (error: unknown) {
      const errorMessage = handleError(error, 'Transcription', 'Failed to refine transcription');
      return {
        success: false,
        error: errorMessage,
      };
    }
  }

  /**
   * Get supported languages for transcription
   * @returns List of supported language codes and names
   */
  static async getSupportedLanguages(): Promise<SupportedLanguagesResponse> {
    try {
      const baseUrl = await this.getBaseUrl();
      const response = await fetch(`${baseUrl}/api/v1/transcription/languages`, {
        headers: await getAuthHeadersFromStorage(),
      });
      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          error: data.error || 'Failed to fetch supported languages',
        };
      }

      return {
        success: true,
        data: data.data,
      };
    } catch (error: unknown) {
      const errorMessage = handleError(
        error,
        'Transcription',
        'Failed to fetch supported languages'
      );
      return {
        success: false,
        error: errorMessage,
      };
    }
  }

  /**
   * Format transcription segments as SRT subtitle format
   * @param segments - Transcription segments
   * @returns SRT formatted string
   */
  static formatAsSRT(segments: TranscriptionSegment[]): string {
    return segments
      .map((segment, index) => {
        const startTime = this.formatSRTTime(segment.start);
        const endTime = this.formatSRTTime(segment.end);
        return `${index + 1}\n${startTime} --> ${endTime}\n${segment.text}\n`;
      })
      .join('\n');
  }

  /**
   * Format transcription segments as VTT subtitle format
   * @param segments - Transcription segments
   * @returns VTT formatted string
   */
  static formatAsVTT(segments: TranscriptionSegment[]): string {
    const header = 'WEBVTT\n\n';
    const content = segments
      .map((segment) => {
        const startTime = this.formatVTTTime(segment.start);
        const endTime = this.formatVTTTime(segment.end);
        return `${startTime} --> ${endTime}\n${segment.text}\n`;
      })
      .join('\n');
    return header + content;
  }

  /**
   * Format seconds to SRT time format (HH:MM:SS,mmm)
   */
  private static formatSRTTime(seconds: number): string {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    const ms = Math.round((seconds % 1) * 1000);
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')},${ms.toString().padStart(3, '0')}`;
  }

  /**
   * Format seconds to VTT time format (HH:MM:SS.mmm)
   */
  private static formatVTTTime(seconds: number): string {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    const ms = Math.round((seconds % 1) * 1000);
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}.${ms.toString().padStart(3, '0')}`;
  }
}
